import {useTimeUnitTranslation, useTranslation} from '@aboutyou/i18n';
import {capitalize} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES} from '../../../constants/typography';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {Text} from '../../atoms/Text/Text';
import {
  Countdown,
  CountdownSize,
  CountdownUnitProps,
} from '../../molecules/Countdown/Countdown';

export interface PriceBoxSaleCountdownProps {
  endDate: Date;
  backgroundColor?: string;
  className?: string;
}

export const PriceBoxSaleCountdown: FC<PriceBoxSaleCountdownProps> = ({
  endDate,
  backgroundColor,
  className,
}) => {
  const {t} = useTranslation();

  return (
    <Wrapper
      className={className}
      data-testid="PriceBoxCountdown"
      $backgroundColor={backgroundColor}
    >
      <ClockIcon
        size="S"
        asset={GenericIconName.Clock}
        style={{fill: COLORS.White}}
      />
      <Label color="White" size="px12">
        {t('Time left')}
      </Label>
      <Countdown
        endDate={endDate}
        unitComponent={TimeUnitSection}
        containerComponent={CountdownContainer}
      />
    </Wrapper>
  );
};

const TimeUnitSection: FC<CountdownUnitProps> = ({value, unit, size}) => {
  const timeFormat = useTimeUnitTranslation('shorter');
  const testId = `countdown${capitalize(unit)}`;

  const unitLabel = timeFormat[unit] ?? '';

  if ((unit === 'days' && value === '00') || !unitLabel) {
    return null;
  }

  return (
    <TimeUnit size={size} testId={testId} unit={unitLabel} value={value} />
  );
};

const TimeUnit: FC<{
  testId: string;
  unit: string;
  value: string;
  size?: CountdownSize;
}> = ({testId, unit, value}) => {
  return (
    <TimeUnitWrapper data-testid={testId}>
      <Text size="px12" color="White" weight="bold">
        {value}
      </Text>
      <Text size="px12" color="White">
        {unit}
      </Text>
    </TimeUnitWrapper>
  );
};

const Wrapper = styled.div<{
  $backgroundColor?: string;
}>`
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  background-color: ${props =>
    props.$backgroundColor ? props.$backgroundColor : COLORS.White};
`;

const Label = styled(Text)`
  white-space: nowrap;
`;

const CountdownContainer = styled.div`
  display: flex;
  padding: 0 4px;
  margin-left: 4px;
  gap: 4px;
  font-size: ${FONT_SIZES.px12};
  line-height: ${FONT_SIZES.px18};
  color: white;
`;

const ClockIcon = styled(Icon)`
  margin-left: 6px;
`;

const TimeUnitWrapper = styled.div`
  display: flex;
  justify-content: center;
`;
